import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  OnChangeFn,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { Task } from "@/app/types/task";
import { PaginatedResponse } from "@/app/types/api";
import { searchItemsAsync, getAllItemsAsync, EnrichedItem } from "@/lib/indexeddb/database";
import { useSQLiteSync } from "@/hooks/useSQLiteSync";
import { useDebounce } from "@/hooks/useDebounce";
import { useAuthStore } from "@/app/store/authStore";

// Define a type for the custom meta properties
interface CustomColumnMeta {
  label?: string;
  placeholder?: string;
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange' | 'boolean' | 'multiSelect';
  icon?: React.ComponentType<{ className?: string }>;
  options?: Array<{ label: string; value: string | number | boolean }>;
}

// Use intersection type for ExtendedColumnDef
type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
};

interface TaskDataTableProps {
  data: PaginatedResponse<Task>;
  columns: ExtendedColumnDef<Task>[];
  isLoading: boolean;
  pagination: {
    pageIndex: number;
    pageSize: number;
    pageCount: number;
  };
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  onPaginationChange: (pagination: { pageIndex: number; pageSize: number }) => void;
  onSortingChange: (sorting: SortingState) => void;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  userOptions?: Array<{ label: string; value: string }>;
  teamOptions?: Array<{ label: string; value: string }>;
  onRowClick?: (task: Task) => void;
  selectedServiceId?: number | null;
  selectedServiceName?: string | null;
  onServiceFilterChange?: (itemId: number | null, itemName: string | null) => void;
}

const TaskDataTable: React.FC<TaskDataTableProps> = ({
  data,
  columns,
  isLoading,
  pagination,
  sorting,
  columnFilters,
  onPaginationChange,
  onSortingChange,
  onColumnFiltersChange,
  userOptions = [],
  teamOptions = [],
  onRowClick,
  selectedServiceId,
  selectedServiceName,
  onServiceFilterChange,
}) => {
  const navigate = useNavigate();
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  // Service filter search state
  const { isInitialized: isIndexedDBInitialized } = useSQLiteSync();
  const userOrgId = useAuthStore(state => state.userOrgId);
  const [serviceSearchOpen, setServiceSearchOpen] = useState(false);
  const [serviceSearchQuery, setServiceSearchQuery] = useState('');
  const [serviceSearchResults, setServiceSearchResults] = useState<EnrichedItem[]>([]);
  const [isServiceSearching, setIsServiceSearching] = useState(false);
  const debouncedServiceQuery = useDebounce(serviceSearchQuery, 200);

  // Load initial items when service filter popover opens
  useEffect(() => {
    if (serviceSearchOpen && isIndexedDBInitialized && userOrgId && !serviceSearchQuery) {
      const loadInitialItems = async () => {
        setIsServiceSearching(true);
        try {
          const allItems = await getAllItemsAsync(userOrgId);
          const initialItems = allItems.filter(item => item.is_active).slice(0, 20);
          setServiceSearchResults(initialItems);
        } catch (error) {
          console.error('[TaskDataTable] Error loading initial items:', error);
          setServiceSearchResults([]);
        } finally {
          setIsServiceSearching(false);
        }
      };
      loadInitialItems();
    }
  }, [serviceSearchOpen, isIndexedDBInitialized, userOrgId, serviceSearchQuery]);

  // Search items when query changes
  useEffect(() => {
    if (debouncedServiceQuery.length > 1 && isIndexedDBInitialized && userOrgId) {
      const performSearch = async () => {
        setIsServiceSearching(true);
        try {
          const results = await searchItemsAsync(debouncedServiceQuery, userOrgId, 50);
          setServiceSearchResults(results);
        } catch (error) {
          console.error('[TaskDataTable] Item search error:', error);
          setServiceSearchResults([]);
        } finally {
          setIsServiceSearching(false);
        }
      };
      performSearch();
    } else if (debouncedServiceQuery.length === 0 && serviceSearchOpen && isIndexedDBInitialized && userOrgId) {
      const loadInitialItems = async () => {
        setIsServiceSearching(true);
        try {
          const allItems = await getAllItemsAsync(userOrgId);
          const initialItems = allItems.filter(item => item.is_active).slice(0, 20);
          setServiceSearchResults(initialItems);
        } catch (error) {
          console.error('[TaskDataTable] Error loading initial items:', error);
          setServiceSearchResults([]);
        } finally {
          setIsServiceSearching(false);
        }
      };
      loadInitialItems();
    }
  }, [debouncedServiceQuery, isIndexedDBInitialized, userOrgId, serviceSearchOpen]);

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const newSorting = updater instanceof Function ? updater(sorting) : updater;
    onSortingChange(newSorting);
    onPaginationChange({ ...pagination, pageIndex: 0 });
  };

  const handleColumnFiltersChange = (updater: ColumnFiltersState | ((state: ColumnFiltersState) => ColumnFiltersState)): void => {
    const newFilters = updater instanceof Function ? updater(columnFilters) : updater;
    onColumnFiltersChange(newFilters);
    // Reset to first page when filters change
    onPaginationChange({ ...pagination, pageIndex: 0 });
  };

  // Create the table instance
  const table = useReactTable({
    data: data.items,
    columns: columns as ColumnDef<Task>[], // Cast back for useReactTable
    pageCount: Math.ceil(data.total / pagination.pageSize),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
    },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onPaginationChange: onPaginationChange,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const getColumnKey = (column: ExtendedColumnDef<Task>): string => {
    return column.id ?? (column as { accessorKey?: string }).accessorKey ?? '';
  };

  const renderFilterControl = (column: ExtendedColumnDef<Task>): JSX.Element | null => {
    const columnKey = getColumnKey(column);
    const tableColumn = table.getColumn(columnKey);
    const columnMeta = column.meta;

    if (!tableColumn || !column.enableColumnFilter || !columnMeta?.variant) {
      return null;
    }

    const filterValue = tableColumn.getFilterValue();

    if (columnMeta.variant === 'text' || columnMeta.variant === 'number') {
      return (
        <Input
          type={columnMeta.variant === 'number' ? 'number' : 'text'}
          value={(filterValue ?? '') as string}
          onChange={(e) => tableColumn.setFilterValue(e.target.value)}
          placeholder={columnMeta.placeholder || `Filter by ${columnMeta.label}...`}
          className="h-8 w-full max-w-xs"
        />
      );
    }

    if (columnMeta.variant === 'select' && columnMeta.options) {
      const ALL_VALUE = "__ALL__"; // Special value for the "All" option

      // For assigned_to / team columns, use dynamic options
      const options = columnKey === 'assigned_to_user_id' && userOptions.length > 0
        ? userOptions.map(opt => ({ label: opt.label, value: opt.value }))
        : columnKey === 'team_id' && teamOptions.length > 0
        ? teamOptions.map(opt => ({ label: opt.label, value: opt.value }))
        : columnMeta.options;

      return (
        <Select
          value={(filterValue ?? ALL_VALUE) as string} // Default to ALL_VALUE if filter is undefined
          onValueChange={value =>
            tableColumn.setFilterValue(value === ALL_VALUE ? undefined : value)
          }
        >
          <SelectTrigger className="h-8 w-full max-w-xs">
            <SelectValue placeholder={columnMeta.placeholder || `Filter by ${columnMeta.label}...`} />
          </SelectTrigger>
          <SelectContent>
            {/* Use the special value for the "All" item */}
            <SelectItem value={ALL_VALUE}>All</SelectItem>
            {options.map((option) => (
              <SelectItem key={String(option.value)} value={String(option.value)}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    return null;
  };

  return (
    <div className="space-y-4">
      {/* Column Visibility Controls */}
      <div className="flex items-center justify-end gap-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9">
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {columns.map((column) => {
              const columnKey = getColumnKey(column);
              const tableCol = table.getColumn(columnKey);
              // Ensure tableCol exists and getCanHide is callable
              return tableCol?.getCanHide() && (
                <DropdownMenuCheckboxItem
                  key={columnKey}
                  className="capitalize"
                  checked={tableCol.getIsVisible()}
                  onCheckedChange={(value) =>
                    tableCol.toggleVisibility(!!value)
                  }
                >
                  {column.meta?.label || columnKey}
                </DropdownMenuCheckboxItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Row for Individual Column Filters */}
      <div className="flex items-center gap-2 flex-wrap p-2 border rounded-md bg-card">
        {columns.map((column) => (
            column.enableColumnFilter && (
                <div key={getColumnKey(column)} className="flex flex-col space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">
                        {column.meta?.label || getColumnKey(column)}
                    </label>
                    {renderFilterControl(column)}
                </div>
            )
        ))}

        {/* Service (Item) Filter */}
        {onServiceFilterChange && (
          <div className="flex flex-col space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Service</label>
            <Popover open={serviceSearchOpen} onOpenChange={setServiceSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={serviceSearchOpen}
                  className="h-8 w-full max-w-xs justify-between font-normal"
                >
                  <span className="truncate">
                    {selectedServiceName || "All Services"}
                  </span>
                  {selectedServiceId ? (
                    <X
                      className="ml-2 h-3 w-3 shrink-0 opacity-50 hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        onServiceFilterChange(null, null);
                      }}
                    />
                  ) : (
                    <Search className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search services..."
                    value={serviceSearchQuery}
                    onValueChange={setServiceSearchQuery}
                  />
                  <CommandList className="max-h-[300px]">
                    {isServiceSearching && (
                      <div className="p-4 text-center">
                        <Loader2 className="h-4 w-4 animate-spin inline-block" />
                      </div>
                    )}
                    {!isServiceSearching && serviceSearchResults.length === 0 && (
                      <CommandEmpty>No services found.</CommandEmpty>
                    )}
                    <CommandGroup>
                      {serviceSearchResults.map((item) => (
                        <CommandItem
                          key={item.id}
                          value={`item-${item.id}`}
                          onSelect={() => {
                            onServiceFilterChange(item.id, item.name);
                            setServiceSearchOpen(false);
                            setServiceSearchQuery('');
                          }}
                          className="cursor-pointer"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{item.name}</span>
                            {item.sku && (
                              <span className="text-xs text-muted-foreground">
                                Code: {item.sku}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <Loader2 className="mr-2 h-4 w-4 animate-spin inline" />
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={onRowClick ? "hover:bg-muted/50 cursor-pointer" : "hover:bg-muted/50"}
                  onClick={(e) => {
                    // Don't trigger row click if clicking on action buttons
                    if ((e.target as HTMLElement).closest('button')) {
                      return;
                    }
                    onRowClick?.(row.original);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No tasks found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination controls */}
      <div className="flex items-center justify-between w-full">
        <div className="text-sm text-muted-foreground">
          Page {pagination.pageIndex + 1} of {pagination.pageCount || 1}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPaginationChange({ ...pagination, pageIndex: pagination.pageIndex - 1 })}
            disabled={pagination.pageIndex === 0 || isLoading}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPaginationChange({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
            disabled={pagination.pageIndex + 1 >= pagination.pageCount || isLoading}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TaskDataTable;
